export type ApiTestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS'
export type ApiTestBodyType = 'none' | 'json' | 'text' | 'html' | 'form'
export type ApiTestAuthType = 'none' | 'bearer' | 'basic' | 'apikey'
export type ApiTestTab = 'params' | 'auth' | 'headers' | 'body'

export interface ApiTestKVRow { key: string; value: string }
export interface ApiTestAuthConfig {
  type: ApiTestAuthType
  token?: string
  username?: string
  password?: string
  key?: string
  value?: string
  addTo?: 'header' | 'query'
}
export interface ApiTestSelection { id: string; collection: string; name: string }

export interface ApiTestRequestState {
  method: ApiTestMethod
  url: string
  params: ApiTestKVRow[]
  headers: ApiTestKVRow[]
  auth: ApiTestAuthConfig
  body_type: ApiTestBodyType
  body: string
}

export interface ApiTestDraft extends ApiTestRequestState {
  activeTab: ApiTestTab
  selectedRequest: ApiTestSelection | null
  savedFingerprint: string
}

// Deliberately memory-only. API tester drafts can contain credentials and
// request bodies, so route changes may restore them during this SPA lifetime
// without introducing another persistent browser-secret store.
const sessionDrafts = new Map<string, ApiTestDraft>()

function cloneRows(rows: ApiTestKVRow[]): ApiTestKVRow[] {
  return rows.map(row => ({ ...row }))
}

function cloneAuth(auth: ApiTestAuthConfig): ApiTestAuthConfig {
  return { ...auth }
}

export function loadApiTestDraft(scope: string): ApiTestDraft | null {
  const draft = sessionDrafts.get(scope)
  if (!draft) return null
  return {
    ...draft,
    params: cloneRows(draft.params),
    headers: cloneRows(draft.headers),
    auth: cloneAuth(draft.auth),
    selectedRequest: draft.selectedRequest ? { ...draft.selectedRequest } : null,
  }
}

export function saveApiTestDraft(scope: string, draft: ApiTestDraft): void {
  sessionDrafts.set(scope, {
    ...draft,
    params: cloneRows(draft.params),
    headers: cloneRows(draft.headers),
    auth: cloneAuth(draft.auth),
    selectedRequest: draft.selectedRequest ? { ...draft.selectedRequest } : null,
  })
}

export function clearApiTestDraft(scope: string): void {
  sessionDrafts.delete(scope)
}

export function splitApiTestUrl(url: string): { base: string; query: string } {
  const idx = url.indexOf('?')
  if (idx === -1) return { base: url, query: '' }
  return { base: url.slice(0, idx), query: url.slice(idx + 1) }
}

function safeDecode(value: string): string {
  try { return decodeURIComponent(value.replace(/\+/g, ' ')) } catch { return value }
}

export function parseApiTestParams(url: string): ApiTestKVRow[] {
  const { query } = splitApiTestUrl(url)
  if (!query) return []
  return query.split('&').filter(Boolean).map(pair => {
    const eq = pair.indexOf('=')
    if (eq === -1) return { key: safeDecode(pair), value: '' }
    return { key: safeDecode(pair.slice(0, eq)), value: safeDecode(pair.slice(eq + 1)) }
  })
}

export function normalizedApiTestParams(rows: ApiTestKVRow[]): ApiTestKVRow[] {
  return rows.filter(row => row.key.trim()).map(row => ({ key: row.key, value: row.value }))
}

export function buildApiTestUrl(url: string, rows: ApiTestKVRow[]): string {
  const { base } = splitApiTestUrl(url)
  const query = normalizedApiTestParams(rows)
    .map(row => `${encodeURIComponent(row.key)}=${encodeURIComponent(row.value)}`).join('&')
  return query ? `${base}?${query}` : base
}

export function apiTestRequestFingerprint(state: ApiTestRequestState): string {
  return JSON.stringify({
    method: state.method,
    url: buildApiTestUrl(state.url, state.params),
    params: normalizedApiTestParams(state.params),
    headers: cloneRows(state.headers),
    auth: cloneAuth(state.auth),
    body_type: state.body_type,
    body: state.body,
  })
}

export function suggestApiTestRequestName(method: ApiTestMethod, url: string): string {
  try {
    const parsed = new URL(url)
    const target = `${parsed.hostname}${parsed.pathname === '/' ? '' : parsed.pathname}`
    return `${method} ${target}`.slice(0, 120)
  } catch {
    return `${method} isteği`
  }
}
